import { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../../../../auth/firebase.js';

// 客戶端錯誤上報 — fire-and-forget,送 backend log 方便診斷手機端問題
// 同 origin 走 admin-console proxy → backend `/api/client-log`
function reportClientError(context, err, extra) {
    try {
        const payload = {
            level: 'error',
            context,
            message: err?.message || String(err),
            errorName: err?.name || '',
            errorCode: err?.code || '',
            stack: err?.stack ? String(err.stack).slice(0, 1200) : '',
            url: typeof window !== 'undefined' ? window.location.href : '',
            extra: extra || null,
            ...((extra && extra.sessionId) ? { sessionId: extra.sessionId } : {}),
            ...((extra && extra.avatarId) ? { avatarId: extra.avatarId } : {}),
        };
        // 透過 fetch fire-and-forget;不 await,不阻塞 UI
        fetch('/api/client-log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {});
    } catch { /* swallow */ }
}

/**
 * useVoiceChatWS — admin-console preview-call WS + mic + playback hook
 *
 * 連線：ws://<admin-console>/api/websocket/external?spaceId&hostId&did (multi-tenant SIP relay 路由)
 *      admin-console server.js 已在 upgrade event 把 WS 轉送到 backend :8080
 *
 * Init payload：
 *   { type: 'init', token, host: avatarId, isPlayground: true,
 *     callerExt: 'admin-preview-<uid>', uniqueId, sampleRate: 24000,
 *     webSampleRate: 16000, languageCode, enableVad: true, ttsEnabled: true }
 *
 * 後端 source 推斷：沒 qubbySipRelay / 沒 callerPhone → source='ivr_test'
 *
 * 麥克風：AudioWorklet 16kHz mono PCM → base64 → ws.send({type:'input_audio_buffer.append', base64})
 * 播放：PCM 24kHz mono → AudioBufferSourceNode 隊列接續播
 * 中斷：用戶 speech_started 時 stop AudioContext queue
 */
export function useVoiceChatWS({ avatarId, hostId, did, languageCode = 'zh-TW', contactVars = null, campaignId = null, greetingOverride = null }) {
    const [state, setState] = useState('idle');           // idle | connecting | listening | thinking | ai_speaking | user_speaking | ended | error
    const [userSubtitle, setUserSubtitle] = useState('');
    const [aiSubtitle, setAiSubtitle] = useState('');
    const [error, setError] = useState(null);
    const [muted, setMuted] = useState(false);
    const [callStartTime, setCallStartTime] = useState(null);

    const wsRef = useRef(null);
    const audioContextRef = useRef(null);     // playback context (24kHz)
    const recordContextRef = useRef(null);    // record context (16kHz target)
    const workletNodeRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const playbackQueueRef = useRef([]);
    const isPlayingRef = useRef(false);
    const playbackEndTimeRef = useRef(0);
    const activeSourcesRef = useRef([]);      // for interrupt: stop all in-flight sources
    const aiSubtitleAccumRef = useRef('');
    const mutedRef = useRef(false);
    const aiTurnActiveRef = useRef(false);    // 是否處於 AI 講話 turn（用於 turn boundary 偵測）
    const aiAudioPlayingRef = useRef(false);  // AI 是否正在播音訊（給 RMS gating echo cancel 用）
    const aiAudioEndedAtRef = useRef(0);      // AI 上次播完音的時間（給 post-speak echo tail 用）
    // 🔴 2026-09-16 還原：這裡曾經把 TTS 導去 MediaStreamDestination → <audio> 以進入瀏覽器
    //    AEC 的參考路徑（AudioContext 的輸出 echoCancellation 消不掉）。**動機沒錯，手段錯了**：
    //    Chrome 一旦看到 MediaStream 就當成「通訊音訊」處理，會套用它自己的頻寬壓縮 → 實通聽感變悶。
    //    setpod 在他們自己的網頁上走過同一條路、也是因為變悶退回來的。
    //    正解是 `new Audio()` 播「一句一段的完整 WAV」——音質完整，而且媒體元素天生就在 AEC
    //    參考路徑裡，兩者兼得。那需要伺服器的 `last:true` 旗標把整句組回一個 WAV（QubbyLive 已加），
    //    屬於另一件工程，還沒做。在那之前回到 ctx.destination：音質優先，
    //    回音防護暫時靠 QubbyLive 的 240ms 打斷確認 + 下面的 playing 回報鏈。
    // 🔴 playing 回報的閘門：init 送出、後端建立 guest 之後才能送。
    //    websocket.js:326 對「guest 尚未建立、又沒有 token/base64」的訊息一律 UNAUTHORIZED + ws.close()。
    //    上一版把 sink 建在 ensurePlaybackContext()，而 start() 會先做 AudioContext 暖機（在送 init 之前）
    //    → 那時就送出 playing → 連線被後端踢掉 → UI 立刻顯示「通話結束」。2026-09-15 實際炸過一次。
    const canSendPlayingRef = useRef(false);

    useEffect(() => { mutedRef.current = muted; }, [muted]);

    // sync testing-mode props into refs so start() (useCallback) always reads latest
    const contactVarsRef = useRef(contactVars);
    const campaignIdRef = useRef(campaignId);
    const greetingOverrideRef = useRef(greetingOverride);
    useEffect(() => { contactVarsRef.current = contactVars; }, [contactVars]);
    useEffect(() => { campaignIdRef.current = campaignId; }, [campaignId]);
    useEffect(() => { greetingOverrideRef.current = greetingOverride; }, [greetingOverride]);

    // === Audio playback helpers ===
    const ensurePlaybackContext = useCallback(() => {
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            // Android Chrome 部分機型不接受非原生 sample rate(原生 48000)→ fallback 不指定 rate
            try {
                audioContextRef.current = new AudioContext({ sampleRate: 24000 });
            } catch (e) {
                reportClientError('ensurePlaybackContext.24000', e);
                try {
                    audioContextRef.current = new AudioContext();
                } catch (e2) {
                    reportClientError('ensurePlaybackContext.fallback', e2);
                    throw e2;
                }
            }
        }
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume().catch((e) => reportClientError('ensurePlaybackContext.resume', e));
        }
        return audioContextRef.current;
    }, []);

    /**
     * 回報「AI 音訊正在播 / 播完」給後端 → LocalLiveAdapter → QubbyLive。
     * 伺服器的打斷入口守門是 `streaming["on"] or streaming["playing"]`：地端 TTS 產得快、
     * 客戶端播得慢，整句常已產完而前端還在播 10~18 秒；沒送這個，客人開口時伺服器判定
     * 「沒在講話」→ **不打斷**（剩餘音訊照送、工具往返不中止）。
     * 🔴 一定要等 canSendPlayingRef（init 已送出）才送，否則後端當成未授權直接關線。
     */
    const playingStateRef = useRef(false);
    const playingOffTimerRef = useRef(null);
    const sendPlaying = useCallback((on) => {
        if (!canSendPlayingRef.current) return;
        const want = !!on;
        const emit = (v) => {
            if (playingStateRef.current === v) return;   // 狀態沒變不重送
            playingStateRef.current = v;
            try {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ type: 'playing', on: v }));
                }
            } catch (e) { /* noop */ }
        };
        if (want) {
            // 開播：立刻報。等於「AI 現在有聲音」——延遲會讓打斷判定漏掉開頭。
            if (playingOffTimerRef.current) { clearTimeout(playingOffTimerRef.current); playingOffTimerRef.current = null; }
            emit(true);
            return;
        }
        // 🔴 播完：**要延遲確認**。音訊是一段一段來的，佇列在兩段之間會瞬間清空 → onended 觸發
        //    → 若立刻回報 false，伺服器就以為 AI 講完了。實測 false→true 相隔 5ms 的抖動，
        //    而那些 false 的空窗正是「客人開口卻沒被判成打斷」的縫（isAISpeaking 被錯誤清掉）。
        //    250ms 內有新音訊 → 取消這次 false；真的沒有下一段才報播完。
        if (playingOffTimerRef.current) clearTimeout(playingOffTimerRef.current);
        playingOffTimerRef.current = setTimeout(() => {
            playingOffTimerRef.current = null;
            emit(false);
        }, 250);
    }, []);

    const playPCMChunk = useCallback((float32Array) => {
        const ctx = ensurePlaybackContext();
        const buffer = ctx.createBuffer(1, float32Array.length, 24000);
        buffer.copyToChannel(float32Array, 0);

        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        if (!aiAudioPlayingRef.current) sendPlaying(true);               // 這批第一段 → 通知伺服器

        const now = ctx.currentTime;
        // 佇列已空（idle，新一輪起播，如開場白第一個 chunk）→ 給 ~0.12s lead，
        // 避免排程在 currentTime 時開頭被削（前兩個字聽不清）。連續 chunk 維持無縫銜接（不加 lead）。
        const FRESH_START_LEAD = 0.12;
        const startAt = playbackEndTimeRef.current > now ? playbackEndTimeRef.current : now + FRESH_START_LEAD;
        source.start(startAt);
        playbackEndTimeRef.current = startAt + buffer.duration;
        activeSourcesRef.current.push(source);

        aiAudioPlayingRef.current = true;     // echo cancel：mic gating 用
        source.onended = () => {
            activeSourcesRef.current = activeSourcesRef.current.filter((s) => s !== source);
            if (activeSourcesRef.current.length === 0 && playbackQueueRef.current.length === 0) {
                isPlayingRef.current = false;
                aiAudioPlayingRef.current = false;
                aiAudioEndedAtRef.current = Date.now();  // 標記播完時間，給 echo tail 用
                sendPlaying(false);
                setState((s) => (s === 'ai_speaking' ? 'listening' : s));
            }
        };
    }, [ensurePlaybackContext, sendPlaying]);

    const stopAllPlayback = useCallback(() => {
        activeSourcesRef.current.forEach((src) => {
            try { src.onended = null; src.stop(); } catch (e) {}
        });
        activeSourcesRef.current = [];
        playbackQueueRef.current = [];
        playbackEndTimeRef.current = 0;
        const wasPlaying = isPlayingRef.current || aiAudioPlayingRef.current;
        isPlayingRef.current = false;
        aiAudioPlayingRef.current = false;
        aiAudioEndedAtRef.current = Date.now();
        if (wasPlaying) sendPlaying(false);
    }, [sendPlaying]);

    // === Decode incoming base64 PCM (int16 LE @ 24kHz) to Float32 ===
    const decodeBase64PCM = useCallback((base64) => {
        const binary = atob(base64);
        const buf = new ArrayBuffer(binary.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
        const int16 = new Int16Array(buf);
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;
        return float32;
    }, []);

    // === Mic capture (AudioWorklet 16kHz mono) → ws ===
    const startMic = useCallback(async () => {
        if (workletNodeRef.current) return;

        // 1) Secure context check（HTTP origin 在 Chrome 預設會 block mediaDevices）
        if (!window.isSecureContext) {
            const err = new Error('insecure_context');
            err.code = 'insecure_context';
            throw err;
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            const err = new Error('mediaDevices unavailable');
            err.code = 'no_media_api';
            throw err;
        }

        // 2) Request mic — classify failures
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: false,
                },
            });
        } catch (gumErr) {
            const name = gumErr.name || '';
            const err = new Error(gumErr.message || name);
            if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
                err.code = 'permission_denied';
            } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
                err.code = 'no_device';
            } else if (name === 'NotReadableError' || name === 'TrackStartError') {
                err.code = 'device_busy';
            } else if (name === 'SecurityError') {
                err.code = 'insecure_context';
            } else {
                err.code = 'unknown';
            }
            err.originalName = name;
            reportClientError('getUserMedia', err, { avatarId, originalName: name });
            throw err;
        }
        mediaStreamRef.current = stream;

        let ctx;
        try {
            ctx = new AudioContext({ sampleRate: 16000 });
        } catch (e1) {
            reportClientError('record.AudioContext.16000', e1, { avatarId });
            try { ctx = new AudioContext(); } catch (e2) {
                reportClientError('record.AudioContext.default', e2, { avatarId });
                throw e2;
            }
        }
        recordContextRef.current = ctx;

        // Worklet 算 RMS 一起送 main thread，給 state-aware echo gating 用
        const workletCode = `
            class RecorderProcessor extends AudioWorkletProcessor {
                process(inputs) {
                    const ch = inputs[0][0];
                    if (!ch) return true;
                    // RMS
                    let sum = 0;
                    for (let i = 0; i < ch.length; i++) sum += ch[i] * ch[i];
                    const rms = Math.sqrt(sum / ch.length);
                    this.port.postMessage({ audio: new Float32Array(ch), rms });
                    return true;
                }
            }
            registerProcessor('preview-recorder', RecorderProcessor);
        `;
        const blob = new Blob([workletCode], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        await ctx.audioWorklet.addModule(url);

        const node = new AudioWorkletNode(ctx, 'preview-recorder');
        workletNodeRef.current = node;

        // === Echo cancellation gating thresholds ===
        // Browser echoCancellation:true 已先把喇叭外漏消大部分（AEC 殘留通常 < 0.01）
        // 我們只要再加薄一層 RMS gate 把殘留 + 底噪過掉 → 不擋真實人聲
        //
        // RMS 經驗值（16kHz mono PCM，browser AEC 已開）：
        //   0.001-0.005  靜音底噪
        //   0.005-0.015  AEC 殘留（AI 喇叭聲沒被完全消掉的部分）
        //   0.02-0.05    一般說話（含戴耳機）
        //   0.05-0.15    清楚近 mic 人聲
        //   0.15+        大聲說話
        //
        // AI speaking 時門檻 0.025 — 比 AEC 殘留高 + 真實 barge-in 輕鬆過
        const RMS_THRESHOLD_IDLE = 0.003;
        const RMS_THRESHOLD_AI_SPEAKING = 0.025;
        const ECHO_TAIL_MS = 250;  // AI 講完後續 echo tail（喇叭物理延遲）— 不要拉太久

        node.port.onmessage = (event) => {
            const { audio, rms } = event.data || {};
            if (!audio || mutedRef.current) return;
            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

            // === State-aware RMS gating（軟體 echo cancellation）===
            const isAiActive = aiAudioPlayingRef.current
                || (Date.now() - aiAudioEndedAtRef.current < ECHO_TAIL_MS);
            const threshold = isAiActive ? RMS_THRESHOLD_AI_SPEAKING : RMS_THRESHOLD_IDLE;
            if (rms < threshold) {
                // 低於門檻：當靜音處理 — 送一段空 PCM 維持連線但不觸發 backend VAD
                const silence = new Float32Array(audio.length).fill(0);
                const silenceBase64 = floatToBase64PCM(silence);
                wsRef.current.send(JSON.stringify({
                    type: 'input_audio_buffer.append',
                    audio: silenceBase64,
                    base64: silenceBase64,
                }));
                return;
            }

            // resample if needed
            let pcm = audio;
            if (ctx.sampleRate !== 16000) {
                pcm = resample(audio, ctx.sampleRate, 16000);
            }
            const base64 = floatToBase64PCM(pcm);
            wsRef.current.send(JSON.stringify({
                type: 'input_audio_buffer.append',
                audio: base64,
                base64,
            }));
        };

        const source = ctx.createMediaStreamSource(stream);
        source.connect(node);
    }, []);

    const stopMic = useCallback(() => {
        try { workletNodeRef.current?.disconnect(); } catch {}
        try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        try { recordContextRef.current?.close(); } catch {}
        workletNodeRef.current = null;
        mediaStreamRef.current = null;
        recordContextRef.current = null;
    }, []);

    // === WS event handler — backend send SSE-format「data: {...}\n\n」===
    const handleServerEvent = useCallback((event) => {
        try {
            let raw = event.data;
            if (typeof raw !== 'string') return;
            // SSE prefix「data: 」剝掉
            if (raw.startsWith('data:')) {
                raw = raw.replace(/^data:\s*/, '').trim();
            }
            if (!raw.startsWith('{')) return;
            const data = JSON.parse(raw);
            const t = data.type;

            // === type:'stream' — AI audio + subtitle 同一事件 ===
            if (t === 'stream') {
                const audioData = data.content?.audioData;
                const subtitle = data.content?.subtitle;

                // Turn boundary：上一輪結束（aiTurnActiveRef=false）後第一個 chunk = 新 turn
                // → 清掉舊 AI 字幕，避免兩輪疊在一起
                if (!aiTurnActiveRef.current && (audioData || subtitle)) {
                    aiSubtitleAccumRef.current = '';
                    setAiSubtitle('');
                    aiTurnActiveRef.current = true;
                }

                if (audioData && typeof audioData === 'string') {
                    try {
                        const pcm = decodeBase64PCM(audioData);
                        playPCMChunk(pcm);
                        isPlayingRef.current = true;
                        setState((s) => (s === 'thinking' || s === 'listening' || s === 'user_speaking' || s === 'connecting' ? 'ai_speaking' : s));
                    } catch (e) {
                        console.warn('[preview-call] decode pcm fail:', e.message);
                    }
                }

                if (subtitle && typeof subtitle === 'string') {
                    aiSubtitleAccumRef.current += subtitle;
                    setAiSubtitle(aiSubtitleAccumRef.current);
                }
                return;
            }

            // === type:'transcript' role:'user' — 用戶 ASR 字幕 ===
            // 用戶開口 = AI turn 即將開始 → 標記 turn 結束、清舊 AI 字幕
            if (t === 'transcript' && data.role === 'user') {
                const txt = data.text || '';
                if (txt) setUserSubtitle(txt);
                aiTurnActiveRef.current = false;
                aiSubtitleAccumRef.current = '';
                setAiSubtitle('');
                return;
            }

            // === type:'textDone' — AI 該 turn 完整 sentence（覆蓋串流累積） ===
            // 不 reset aiTurnActiveRef — _sendGreeting 一啟動就送 stream(完整) + textDone(完整)，
            // 後續 Gemini Live 真實 audio chunks 才陸續進來。若 textDone reset aiTurnActiveRef，
            // 下個 audio stream 會把 accum 清空，丟掉「蘇先生您好」前綴。
            // 讓 aiTurnActiveRef 只在 transcript role=user 或 stop/cancel 才 reset。
            if (t === 'textDone') {
                const finalText = data.content?.sentence || data.text || aiSubtitleAccumRef.current;
                if (finalText) {
                    aiSubtitleAccumRef.current = finalText;
                    setAiSubtitle(finalText);
                }
                return;
            }

            // === type:'response' — 後端的純文字 echo（用 textDone 主控，這個忽略）===
            if (t === 'response') {
                return;
            }

            // === type:'stop' / type:'cancel' — backend 中斷 AI ===
            // _onInterrupted() 送 'stop' (line 1228 IvrLiveFlowController)
            // 立刻 stop AI 播放 + 清字幕 + 切 user_speaking 狀態（barge-in）
            if (t === 'stop' || t === 'cancel') {
                stopAllPlayback();
                aiSubtitleAccumRef.current = '';
                setAiSubtitle('');
                aiTurnActiveRef.current = false;
                setState((s) => (s === 'ai_speaking' ? 'user_speaking' : s));
                return;
            }

            // === type:'session.created' / 'session_end' ===
            if (t === 'session.created') {
                return;
            }
            // hang_up = SurveySipAdapter / 新版 external 統一掛斷訊號(audio 播完才送,不用再等 echo)
            if (t === 'session_end' || t === 'end_call' || t === 'hang_up') {
                setState('ended');
                return;
            }

            // === type:'waitingForGreeting' ===
            if (t === 'waitingForGreeting') {
                return;
            }
        } catch (e) {
            console.warn('[preview-call] event parse fail:', e.message, 'raw:', String(event.data).substring(0, 100));
        }
    }, [decodeBase64PCM, playPCMChunk, stopAllPlayback]);

    // === Public: start session ===
    const start = useCallback(async () => {
        const cvNow = contactVarsRef.current;
        const stackTop = new Error().stack?.split('\n').slice(1, 5).join(' | ');
        console.log('[useVoiceChatWS] 🚀 start() called:', {
            contactVarsAtCallTime: cvNow ? Object.keys(cvNow) : 'NULL',
            campaignIdAtCallTime: campaignIdRef.current,
            greetingOverrideAtCallTime: greetingOverrideRef.current?.slice(0, 40),
            calledFrom: stackTop,
        });
        if (!avatarId) { setError(new Error('missing avatarId')); return; }
        setError(null);
        setState('connecting');

        // 暖機 AudioContext —— 避免第一個 audio chunk 到時 context 還 suspended/cold,
        // 第一個音節(尤其開場白姓氏「邱/賴/郭」這種短促音)被吞掉。
        // 必須在 user gesture(onClick)context 內 resume(),否則 browser autoplay policy 擋。
        try {
            const ctx = ensurePlaybackContext();
            const silentBuf = ctx.createBuffer(1, 480, 24000);   // 20ms 靜音
            const silentSrc = ctx.createBufferSource();
            silentSrc.buffer = silentBuf;
            silentSrc.connect(ctx.destination);
            silentSrc.start(ctx.currentTime);
            playbackEndTimeRef.current = ctx.currentTime + (480 / 24000);
        } catch (e) {
            console.warn('[useVoiceChatWS] audio prewarm failed:', e.message);
        }
        setUserSubtitle('');
        setAiSubtitle('');
        aiSubtitleAccumRef.current = '';

        try {
            // Get Firebase token
            const user = auth.currentUser;
            if (!user) throw new Error('not authenticated');
            const token = await user.getIdToken();
            const uid = user.uid;

            // Open WS (relative URL → admin-console server.js upgrades + proxies to backend)
            const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
            const params = new URLSearchParams();
            params.set('spaceId', avatarId);
            if (hostId) params.set('hostId', hostId);
            if (did) params.set('did', did);
            // Testing 模式：contactVars 帶會員資料給 LiveBuilder 注入「# 通話對象資料」section
            // campaignId 帶假 ID 觸發「# 通話方向 — 外播」(只外播模式才帶)
            // 從 ref 讀，避免 useCallback closure stale
            const cv = contactVarsRef.current;
            const cid = campaignIdRef.current;
            const go = greetingOverrideRef.current;
            if (cv && typeof cv === 'object' && Object.keys(cv).length > 0) {
                try { params.set('contactVarsJson', JSON.stringify(cv)); } catch {}
            }
            if (cid) params.set('campaignId', cid);
            if (go && typeof go === 'string') params.set('greetingOverride', go);
            const url = `${proto}//${location.host}/api/websocket/external?${params.toString()}`;

            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                const initPayload = {
                    type: 'init',
                    token,
                    host: avatarId,
                    isPlayground: true,
                    isTextMode: false,
                    callerExt: `admin-preview-${uid.substring(0, 8)}`,
                    uniqueId: `preview-${Date.now()}`,
                    sampleRate: 24000,
                    webSampleRate: 16000,
                    languageCode,
                    enableVad: true,
                    ttsEnabled: true,
                };
                ws.send(JSON.stringify(initPayload));
                // init 已送出 → 後端會建立 guest，之後送 playing 才不會被當成未授權而關線
                canSendPlayingRef.current = true;

                setCallStartTime(Date.now());
                setState('listening');
                startMic().catch((err) => {
                    console.error('[preview-call] startMic fail:', err.code || err.name, err.message);
                    setError(err);
                    setState('error');
                    // mic 起不來 → 不繼續通話，關掉 WS
                    try { wsRef.current?.close(1000, 'mic_failed'); } catch {}
                });
            };

            ws.onmessage = handleServerEvent;

            ws.onerror = (err) => {
                console.error('[preview-call] ws error:', err);
                reportClientError('ws.onerror', err || new Error('ws.onerror fired'), { avatarId });
                setError(new Error('WebSocket error'));
                setState('error');
            };

            ws.onclose = (ev) => {
                // 異常關 (code !== 1000 / !== 1005-with-clean) 上報,診斷手機端 1005 飆 close 問題
                const code = ev?.code ?? 0;
                const reason = ev?.reason || '';
                const wasClean = ev?.wasClean === true;
                if (!wasClean && code !== 1000) {
                    reportClientError('ws.onclose.abnormal', new Error(`code=${code} reason=${reason} wasClean=${wasClean}`), {
                        avatarId, code, reason, wasClean,
                        connectionAgeMs: callStartTime ? (Date.now() - callStartTime) : null,
                    });
                }
                // 'idle' 也保留 —— modal close 走 reset() 流程時,reset 先 setState('idle'),
                // 但 ws.close() 觸發的 onclose callback 是 microtask 後 async 才 fire,
                // 若不保留 'idle' 會被強制覆寫成 'ended',導致重開 modal 看到 ended UI(關閉按鈕)
                setState((s) => (s === 'ended' || s === 'error' || s === 'idle' ? s : 'ended'));
            };

            // Heartbeat
            const hb = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) ws.send('heartbeat');
                else clearInterval(hb);
            }, 25000);
        } catch (e) {
            console.error('[preview-call] start fail:', e);
            setError(e);
            setState('error');
        }
    }, [avatarId, hostId, did, languageCode, handleServerEvent, startMic]);

    // === Public: hang up ===
    const hangUp = useCallback(() => {
        try {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'end_call', reason: 'user_hangup' }));
                wsRef.current.close(1000, 'user_hangup');
            }
        } catch {}
        canSendPlayingRef.current = false;
        if (playingOffTimerRef.current) { clearTimeout(playingOffTimerRef.current); playingOffTimerRef.current = null; }
        playingStateRef.current = false;
        stopAllPlayback();
        stopMic();
        try { audioContextRef.current?.close(); } catch {}
        audioContextRef.current = null;
        wsRef.current = null;
        setState('ended');
    }, [stopAllPlayback, stopMic]);

    // reset = hangUp + 進一步把 state 清回 'idle' + 清字幕,給 modal 關閉時用,
    // 確保下次重開模擬通話是全新狀態(不會看到上次 ended UI)
    const reset = useCallback(() => {
        hangUp();
        setState('idle');
        setUserSubtitle('');
        setAiSubtitle('');
        aiSubtitleAccumRef.current = '';
        setError(null);
        setCallStartTime(null);
    }, [hangUp]);

    // cleanup on unmount
    useEffect(() => () => hangUp(), []);  // eslint-disable-line

    return {
        state,
        userSubtitle,
        aiSubtitle,
        error,
        muted,
        setMuted,
        callStartTime,
        start,
        hangUp,
        reset,
    };
}

// === Utils ===
function floatToBase64PCM(float32) {
    const buf = new ArrayBuffer(float32.length * 2);
    const view = new DataView(buf);
    for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]));
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

function resample(input, srcRate, dstRate) {
    if (srcRate === dstRate) return input;
    const ratio = srcRate / dstRate;
    const outLen = Math.floor(input.length / ratio);
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
        const idx = i * ratio;
        const lo = Math.floor(idx);
        const hi = Math.min(lo + 1, input.length - 1);
        const frac = idx - lo;
        out[i] = input[lo] * (1 - frac) + input[hi] * frac;
    }
    return out;
}
